/**
 * The organizer's registration questions - `EX-REG01`, `docs/29` §5.3.
 * Wire types and the pure helpers both surfaces share: the door that asks
 * them and the console that writes them.
 */

export const REGISTRATION_QUESTION_KINDS = ["text", "choice", "number", "file"] as const;
export type RegistrationQuestionKind = (typeof REGISTRATION_QUESTION_KINDS)[number];

/** The server's limits (`contest-registration-questions.ts`), restated for the forms. */
export const REGISTRATION_QUESTIONS_MAX = 10;
export const REGISTRATION_LABEL_MAX = 120;
export const REGISTRATION_HELP_MAX = 200;
export const REGISTRATION_OPTIONS_MAX = 20;
export const REGISTRATION_OPTION_MAX = 60;
export const REGISTRATION_TEXT_MAX = 200;
export const REGISTRATION_FILE_MAX_BYTES = 5 * 1024 * 1024;
export const REGISTRATION_FILE_ACCEPT = ".png,.jpg,.jpeg,.webp,.pdf";

/** One question as the contest page prints it. */
export type RegistrationQuestion = {
  id: string;
  kind: RegistrationQuestionKind;
  label: string;
  help: string | null;
  required: boolean;
  options: string[];
  min: number | null;
  max: number | null;
};

/** One question as the console reads it: how many answered, and whether it is locked. */
export type TeamRegistrationQuestion = RegistrationQuestion & {
  answeredCount: number;
  locked: boolean;
  choiceCounts?: { option: string; count: number }[];
};

export type TeamRegistrationQuestions = {
  contestStatus: string;
  registrationOpened: boolean;
  editable: boolean;
  questions: TeamRegistrationQuestion[];
  participants: number;
  answeredParticipants: number;
  completeParticipants: number;
};

/** One question as the console sends it: `id` names an existing one. */
export type RegistrationQuestionInput = {
  id?: string;
  kind: RegistrationQuestionKind;
  label: string;
  help?: string | null;
  required: boolean;
  options?: string[];
  min?: number | null;
  max?: number | null;
};

export type RegistrationFile = { id: string; filename: string; mimeType: string; sizeBytes: number };

/** One answer as either reader sees it; a file by its name, `fileRemoved` after retention. */
export type RegistrationAnswerView = {
  questionId: string;
  text?: string;
  choice?: string;
  number?: number;
  file?: RegistrationFile | null;
  fileRemoved?: boolean;
};

export type RegistrationAnswerInput = {
  questionId: string;
  text?: string;
  choice?: string;
  number?: number;
  fileId?: string;
};

export type MyRegistrationAnswers = {
  answers: RegistrationAnswerView[];
  missingRequired: string[];
  editable: boolean;
};

export type TeamRegistrationAnswers = {
  questions: RegistrationQuestion[];
  items: { userId: string; answers: RegistrationAnswerView[]; missingRequired: string[] }[];
};

/** The contest page's word to a participant: `null` when the contest asks nothing. */
export type ViewerRegistrationState = {
  answered: boolean;
  missingRequired: string[];
  editable: boolean;
} | null;

/** The form's draft of one answer, keyed by question id. */
export type RegistrationDraft = Record<
  string,
  { text?: string; choice?: string; number?: string; file?: RegistrationFile | null }
>;

/** A saved set of answers as the form's draft. */
export function draftFromAnswers(answers: readonly RegistrationAnswerView[]): RegistrationDraft {
  const draft: RegistrationDraft = {};
  for (const answer of answers) {
    draft[answer.questionId] = {
      ...(answer.text !== undefined ? { text: answer.text } : {}),
      ...(answer.choice !== undefined ? { choice: answer.choice } : {}),
      ...(answer.number !== undefined ? { number: String(answer.number) } : {}),
      ...(answer.file ? { file: answer.file } : {}),
    };
  }
  return draft;
}

/** The draft as the request body: only what is filled in, each in its kind's field. */
export function answersFromDraft(
  questions: readonly RegistrationQuestion[],
  draft: RegistrationDraft,
): RegistrationAnswerInput[] {
  const answers: RegistrationAnswerInput[] = [];
  for (const question of questions) {
    const entry = draft[question.id];
    if (!entry) continue;
    switch (question.kind) {
      case "text":
        if (entry.text?.trim()) answers.push({ questionId: question.id, text: entry.text.trim() });
        break;
      case "choice":
        if (entry.choice) answers.push({ questionId: question.id, choice: entry.choice });
        break;
      case "number": {
        const value = entry.number?.trim() ? Number(entry.number) : NaN;
        if (Number.isFinite(value)) answers.push({ questionId: question.id, number: value });
        break;
      }
      case "file":
        if (entry.file) answers.push({ questionId: question.id, fileId: entry.file.id });
        break;
    }
  }
  return answers;
}

/** The required questions a draft leaves open, or a number outside its bounds. */
export function draftProblems(questions: readonly RegistrationQuestion[], draft: RegistrationDraft): Set<string> {
  const answered = new Set(answersFromDraft(questions, draft).map((answer) => answer.questionId));
  const problems = new Set<string>();
  for (const question of questions) {
    if (question.required && !answered.has(question.id)) problems.add(question.id);
    if (question.kind === "number" && draft[question.id]?.number?.trim()) {
      const value = Number(draft[question.id]?.number);
      if (
        !Number.isFinite(value) ||
        (question.min !== null && value < question.min) ||
        (question.max !== null && value > question.max)
      ) {
        problems.add(question.id);
      }
    }
  }
  return problems;
}

/** An answer as one line of text, for the console row and the contestant's summary. */
export function answerText(answer: RegistrationAnswerView | undefined): string | null {
  if (!answer) return null;
  if (answer.text !== undefined) return answer.text;
  if (answer.choice !== undefined) return answer.choice;
  if (answer.number !== undefined) return String(answer.number);
  if (answer.file) return answer.file.filename;
  return null;
}

/** "2.4 MB", "640 KB": a file's size for a person. */
export function fileSizeLabel(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
